import { useState, useRef, useEffect } from 'react';
import { useUser } from '@clerk/react';
import { useAuthStore } from '@/store/auth-store.js';
import {
  useGetMyPendingDeliberations,
} from '@edison/api-client-react';
import { useQuery } from '@tanstack/react-query';
import { customInstance } from '@edison/api-client-react';
import {
  AlertCircle,
  Plus,
  CheckCircle2,
  Clock,
  TrendingUp,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Users,
  ShieldAlert,
  Scale,
} from 'lucide-react';
import { useLocation, Link } from 'wouter';
import { Card } from '@/components/ui/card.js';
import { SkeletonCard } from '@/components/ui/skeleton.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { SetupChecklist } from '@/components/tour/index.js';
import { formatFy } from '@/lib/utils.js';
import { WeekHeatmapChart } from '@/components/charts/week-heatmap-chart.js';
import { SdlcBreakdown } from '@/components/sdlc-breakdown.js';
import {
  type DefensibilityCriterion,
  type DefensibilityScore,
  SCORE_META,
} from '@/lib/defensibility.js';

// ─── Greeting helpers ─────────────────────────────────────────────────────────
function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function formatTodayLong(): string {
  return new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
}

// ─── Current income year helper ───────────────────────────────────────────────
// Australian FY ends June 30. Month >= 6 (July+) → new FY started this July → FY = year + 1.
// Jan-June (month 0-5) → still in FY that started last July → FY = year.
function currentIncomeYear(): string {
  const now = new Date();
  const year = now.getFullYear();
  return now.getMonth() >= 6 ? `FY${year + 1}` : `FY${year}`;
}

// ─── Format helpers ───────────────────────────────────────────────────────────
function formatAUD(amount: number): string {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}k`;
  return `$${amount.toFixed(0)}`;
}

function formatMonthLabel(yyyyMm: string): string {
  const [year, month] = yyyyMm.split('-');
  const date = new Date(`${year}-${month}-01`);
  return date.toLocaleDateString('en-AU', { month: 'short' }).toUpperCase();
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({
  label,
  value,
  secondary,
  unit,
  icon: Icon,
  href,
  isLoading,
  alert = false,
}: {
  label: string;
  value: number | string;
  secondary?: string;
  unit?: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  isLoading: boolean;
  alert?: boolean;
}) {
  const [, navigate] = useLocation();
  return (
    <Card
      variant="hero"
      onClick={() => navigate(href)}
      className={`text-left w-full cursor-pointer group transition-all duration-200 ${
        alert ? 'border-warning/30 bg-warning/[0.03]' : ''
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
            alert
              ? 'bg-warning/10 group-hover:bg-warning/15'
              : 'bg-muted group-hover:bg-muted/70'
          }`}
          aria-hidden="true"
        >
          <Icon className={`h-4 w-4 ${alert ? 'text-warning' : 'text-muted-foreground'}`} />
        </div>
      </div>
      {isLoading ? (
        <div className="h-8 w-20 rounded-md animate-pulse bg-[oklch(0.93_0.006_250)]" aria-hidden="true" />
      ) : (
        <p className={`text-3xl font-semibold font-mono tabular-nums leading-none ${alert ? 'text-warning-foreground' : 'text-foreground'}`}>
          {value}
          {unit && <span className="text-base font-medium ml-1 opacity-60">{unit}</span>}
        </p>
      )}
      {secondary && !isLoading && (
        <p className="text-xs text-muted-foreground mt-1.5">{secondary}</p>
      )}
    </Card>
  );
}

// ─── Claim health banner ──────────────────────────────────────────────────────
function ClaimHealthBanner({
  incomeYear,
  totalLabour,
  totalGrossLabour,
  totalNonLabour,
  avgRdRate,
  approvedCount,
  totalEligibleHours,
  isLoading,
}: {
  incomeYear: string;
  totalLabour: number;
  totalGrossLabour: number;
  totalNonLabour: number;
  avgRdRate: number;
  approvedCount: number;
  totalEligibleHours: number;
  isLoading: boolean;
}) {

  if (isLoading) {
    return <div className="h-32 rounded-xl skeleton" />;
  }

  const hasAnyExpenditure = totalGrossLabour > 0 || totalNonLabour > 0;
  if (!hasAnyExpenditure) return null;

  const classifiedLabour = totalLabour;
  const nonLabour = totalNonLabour;
  const unclassifiedLabour = Math.max(0, totalGrossLabour - totalLabour);
  const totalBar = totalGrossLabour + nonLabour;

  const claimEstimate = classifiedLabour + nonLabour;
  const maxPossible = totalGrossLabour + nonLabour;
  const showMax = maxPossible - claimEstimate > 1;

  const classifiedPct = totalBar > 0 ? (classifiedLabour / totalBar) * 100 : 0;
  const nonLabourPct = totalBar > 0 ? (nonLabour / totalBar) * 100 : 0;

  // Muted neutral — the dashboard isn't the right place to alarm on R&D rate
  const rateColor = 'text-foreground';

  return (
    <Card variant="static" className="p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            R&D claim health — {formatFy(incomeYear)}
          </p>
          <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
            <span className="text-4xl font-semibold font-mono tabular-nums text-primary">
              {formatAUD(claimEstimate)}
            </span>
            <span className="text-sm text-muted-foreground">current estimate</span>
            {showMax && (
              <>
                <span className="text-muted-foreground/40 hidden sm:inline">·</span>
                <span className="text-2xl font-semibold font-mono tabular-nums text-muted-foreground">
                  {formatAUD(maxPossible)}
                </span>
                <span className="text-sm text-muted-foreground">max possible</span>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-6 shrink-0">
          {avgRdRate > 0 && (
            <div className="text-right">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Avg R&D rate
              </p>
              <p className={`text-2xl font-semibold font-mono tabular-nums ${rateColor}`}>
                {avgRdRate.toFixed(0)}%
              </p>
            </div>
          )}
          {approvedCount > 0 && (
            <div className="text-right">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Eligible hours
              </p>
              <p className="text-2xl font-semibold font-mono tabular-nums text-foreground">
                {new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 }).format(totalEligibleHours)}
                <span className="text-base font-medium ml-0.5 opacity-60">h</span>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="space-y-3">
        <div className="h-3 rounded-full overflow-hidden bg-muted flex">
          {classifiedPct > 0 && (
            <div
              className="h-full bg-primary transition-all duration-700"
              style={{ width: `${classifiedPct}%` }}
            />
          )}
          {nonLabourPct > 0 && (
            <div
              className="h-full bg-blue-400/70 transition-all duration-700"
              style={{ width: `${nonLabourPct}%` }}
            />
          )}
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
          {classifiedLabour > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-sm bg-primary inline-block shrink-0" aria-hidden="true" />
              <span className="font-mono font-semibold text-foreground">{formatAUD(classifiedLabour)}</span>
              <span>classified labour</span>
            </div>
          )}
          {nonLabour > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-sm bg-blue-400/70 inline-block shrink-0" aria-hidden="true" />
              <span className="font-mono font-semibold text-foreground">{formatAUD(nonLabour)}</span>
              <span>non-labour costs</span>
            </div>
          )}
          {unclassifiedLabour > 1 && (
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-sm bg-muted-foreground/25 inline-block shrink-0" aria-hidden="true" />
              <span className="font-mono font-semibold text-foreground">{formatAUD(unclassifiedLabour)}</span>
              <span>unclassified R&D labour</span>
            </div>
          )}
        </div>
      </div>

    </Card>
  );
}

// ─── Chart types ─────────────────────────────────────────────────────────────
type MonthItem = {
  month: string;
  labourExpenditure: number;
  grossLabourExpenditure: number;
  nonLabourExpenditure: number;
  nonLabourBySource: Record<string, number>;
  totalExpenditure: number;
  payrollExpenditure: number;
  projectHours: Array<{ name: string; hours: number }>;
  personHours: Array<{ name: string; hours: number }>;
};

function SimpleRdChart({ items }: { items: MonthItem[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cw, setCw] = useState(600);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const init = el.getBoundingClientRect().width;
    if (init > 0) setCw(init);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setCw(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const W = cw;
  const H = 160;
  const PAD_L = 50;
  const PAD_R = 8;
  const PAD_T = 10;
  const PAD_B = 24;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;
  const n = Math.max(items.length, 1);
  const GAP = Math.max(2, (chartW / n) * 0.2);
  const barW = Math.max(4, chartW / n - GAP);
  const hasPayroll = items.some((m) => m.payrollExpenditure > 0);
  const maxVal = Math.max(...items.map((m) => Math.max(m.labourExpenditure, m.payrollExpenditure)), 1);
  const yTicks = [0, 0.5, 1].map((t) => ({
    pct: t,
    value: maxVal * t,
    y: PAD_T + chartH * (1 - t),
  }));

  const tooltipXPct = hoveredIdx !== null ? ((PAD_L + hoveredIdx * (barW + GAP) + barW / 2) / W) * 100 : 0;

  // Payroll line points
  const payrollPoints = items
    .map((m, i) => {
      const cx = PAD_L + i * (barW + GAP) + barW / 2;
      const cy = PAD_T + chartH - (m.payrollExpenditure / maxVal) * chartH;
      return `${cx},${cy}`;
    })
    .join(' ');

  return (
    <div ref={containerRef} className="relative w-full" onMouseLeave={() => setHoveredIdx(null)}>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">R&amp;D Labour</p>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, display: 'block' }} aria-label="Monthly R&D labour expenditure">
        {yTicks.map((tick) => (
          <g key={tick.pct}>
            <line x1={PAD_L} y1={tick.y} x2={W - PAD_R} y2={tick.y} stroke="currentColor" strokeOpacity={tick.pct === 0 ? 0.15 : 0.06} strokeWidth={tick.pct === 0 ? 1 : 0.75} />
            <text x={PAD_L - 5} y={tick.y + 4} textAnchor="end" fontSize={9} fill="currentColor" fillOpacity={0.4}>
              {tick.value > 0 ? formatAUD(tick.value) : ''}
            </text>
          </g>
        ))}
        {items.map((m, i) => {
          const x = PAD_L + i * (barW + GAP);
          const barH = maxVal > 0 ? (m.labourExpenditure / maxVal) * chartH : 0;
          const isHov = hoveredIdx === i;
          return (
            <g key={m.month} onMouseEnter={() => setHoveredIdx(i)} style={{ cursor: 'default' }}>
              {barH > 0 && (
                <rect x={x} y={PAD_T + chartH - barH} width={barW} height={barH}
                  fill="var(--color-primary, #6366f1)" fillOpacity={isHov ? 1 : 0.8} rx={2} />
              )}
              <rect x={x - GAP / 2} y={PAD_T} width={barW + GAP} height={chartH + PAD_B - 2} fill="transparent" />
              <text x={x + barW / 2} y={H - 5} textAnchor="middle" fontSize={9} fill="currentColor" fillOpacity={isHov ? 0.75 : 0.35} fontWeight={isHov ? 600 : 400}>
                {formatMonthLabel(m.month)}
              </text>
            </g>
          );
        })}
        {/* Payroll (wages) overlay line */}
        {hasPayroll && items.length > 1 && (
          <>
            <polyline
              points={payrollPoints}
              fill="none"
              stroke="#f59e0b"
              strokeWidth={2}
              strokeOpacity={0.85}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {items.map((m, i) => {
              if (m.payrollExpenditure === 0) return null;
              const cx = PAD_L + i * (barW + GAP) + barW / 2;
              const cy = PAD_T + chartH - (m.payrollExpenditure / maxVal) * chartH;
              return (
                <circle key={`payroll-${i}`} cx={cx} cy={cy} r={3} fill="#f59e0b" fillOpacity={0.9} stroke="white" strokeWidth={1} />
              );
            })}
          </>
        )}
      </svg>
      {hoveredIdx !== null && (() => {
        const item = items[hoveredIdx];
        if (!item || (item.labourExpenditure === 0 && item.payrollExpenditure === 0)) return null;
        const [yr, mo] = item.month.split('-');
        const monthLabel = new Date(`${yr}-${mo}-01`).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
        const alignRight = tooltipXPct > 70;
        return (
          <div className="absolute z-10 pointer-events-none rounded-lg border bg-popover text-popover-foreground shadow-lg text-xs p-3 space-y-1"
            style={{ bottom: `${PAD_B + 6}px`, left: `${tooltipXPct}%`, transform: alignRight ? 'translateX(-100%)' : 'translateX(-50%)', minWidth: 140 }}>
            <p className="font-semibold">{monthLabel}</p>
            {item.labourExpenditure > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">R&amp;D labour</span>
                <span className="font-mono font-medium">{formatAUD(item.labourExpenditure)}</span>
              </div>
            )}
            {item.grossLabourExpenditure > item.labourExpenditure + 1 && (
              <div className="flex justify-between gap-4 text-muted-foreground/70">
                <span>all approved</span>
                <span className="font-mono">{formatAUD(item.grossLabourExpenditure)}</span>
              </div>
            )}
            {item.nonLabourExpenditure > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">non-labour</span>
                <span className="font-mono font-medium">{formatAUD(item.nonLabourExpenditure)}</span>
              </div>
            )}
            {item.payrollExpenditure > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground" style={{ color: '#f59e0b' }}>Payroll cost (Xero)</span>
                <span className="font-mono font-medium">{formatAUD(item.payrollExpenditure)}</span>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

const STACK_COLORS = [
  '#6366f1', '#10b981', '#f59e0b', '#3b82f6',
  '#ec4899', '#8b5cf6', '#14b8a6', '#f97316',
  '#ef4444', '#84cc16',
] as const;

function ProjectPersonChart({ items }: { items: MonthItem[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cw, setCw] = useState(600);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [mode, setMode] = useState<'project' | 'person'>('project');

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const init = el.getBoundingClientRect().width;
    if (init > 0) setCw(init);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setCw(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const W = cw;
  const H = 180;
  const PAD_L = 50;
  const PAD_R = 8;
  const PAD_T = 10;
  const PAD_B = 24;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;
  const n = Math.max(items.length, 1);
  const GAP = Math.max(2, (chartW / n) * 0.2);
  const barW = Math.max(4, chartW / n - GAP);

  // Collect all unique names in mode order for consistent colour assignment
  const allNames = Array.from(
    new Set(items.flatMap((m) => (mode === 'project' ? m.projectHours : m.personHours).map((p) => p.name))),
  ).sort();
  const colorMap = new Map(allNames.map((name, i) => [name, STACK_COLORS[i % STACK_COLORS.length]!]));

  const maxVal = Math.max(...items.map((m) => (mode === 'project' ? m.projectHours : m.personHours).reduce((s, p) => s + p.hours, 0)), 1);
  const yTicks = [0, 0.5, 1].map((t) => ({
    pct: t,
    value: maxVal * t,
    y: PAD_T + chartH * (1 - t),
  }));

  const tooltipXPct = hoveredIdx !== null ? ((PAD_L + hoveredIdx * (barW + GAP) + barW / 2) / W) * 100 : 0;

  return (
    <div ref={containerRef} className="relative w-full" onMouseLeave={() => setHoveredIdx(null)}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Hours worked on</p>
        <div className="inline-flex items-center rounded-md border text-xs">
          <button onClick={() => setMode('project')} className={`px-2.5 py-1 rounded-l-md transition-colors ${mode === 'project' ? 'bg-muted text-foreground font-medium' : 'text-muted-foreground hover:bg-muted/50'}`}>By project</button>
          <button onClick={() => setMode('person')} className={`px-2.5 py-1 rounded-r-md transition-colors ${mode === 'person' ? 'bg-muted text-foreground font-medium' : 'text-muted-foreground hover:bg-muted/50'}`}>By person</button>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, display: 'block' }} aria-label={`Monthly hours by ${mode}`}>
        {yTicks.map((tick) => (
          <g key={tick.pct}>
            <line x1={PAD_L} y1={tick.y} x2={W - PAD_R} y2={tick.y} stroke="currentColor" strokeOpacity={tick.pct === 0 ? 0.15 : 0.06} strokeWidth={tick.pct === 0 ? 1 : 0.75} />
            <text x={PAD_L - 5} y={tick.y + 4} textAnchor="end" fontSize={9} fill="currentColor" fillOpacity={0.4}>
              {tick.value > 0 ? `${Math.round(tick.value)}h` : ''}
            </text>
          </g>
        ))}
        {items.map((m, i) => {
          const x = PAD_L + i * (barW + GAP);
          const segments = mode === 'project' ? m.projectHours : m.personHours;
          const total = segments.reduce((s, p) => s + p.hours, 0);
          const isHov = hoveredIdx === i;
          let yBottom = PAD_T + chartH;
          return (
            <g key={m.month} onMouseEnter={() => setHoveredIdx(i)} style={{ cursor: 'default' }}>
              {segments.map((seg) => {
                const segH = maxVal > 0 ? (seg.hours / maxVal) * chartH : 0;
                yBottom -= segH;
                return (
                  <rect key={seg.name} x={x} y={yBottom} width={barW} height={segH}
                    fill={colorMap.get(seg.name) ?? '#888'} fillOpacity={isHov ? 0.92 : 0.7} rx={1} />
                );
              })}
              {total === 0 && (
                <rect x={x} y={PAD_T + chartH - 2} width={barW} height={2} fill="currentColor" fillOpacity={0.08} rx={1} />
              )}
              <rect x={x - GAP / 2} y={PAD_T} width={barW + GAP} height={chartH + PAD_B - 2} fill="transparent" />
              <text x={x + barW / 2} y={H - 5} textAnchor="middle" fontSize={9} fill="currentColor" fillOpacity={isHov ? 0.75 : 0.35} fontWeight={isHov ? 600 : 400}>
                {formatMonthLabel(m.month)}
              </text>
            </g>
          );
        })}
      </svg>
      {/* Legend */}
      {allNames.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
          {allNames.map((name) => (
            <div key={name} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="h-2 w-2 rounded-sm inline-block shrink-0" style={{ background: colorMap.get(name) }} />
              {name}
            </div>
          ))}
        </div>
      )}
      {hoveredIdx !== null && (() => {
        const item = items[hoveredIdx];
        if (!item) return null;
        const segments = mode === 'project' ? item.projectHours : item.personHours;
        if (segments.length === 0) return null;
        const [yr, mo] = item.month.split('-');
        const monthLabel = new Date(`${yr}-${mo}-01`).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
        const alignRight = tooltipXPct > 70;
        return (
          <div className="absolute z-10 pointer-events-none rounded-lg border bg-popover text-popover-foreground shadow-lg text-xs p-3 space-y-1"
            style={{ bottom: `${PAD_B + 6}px`, left: `${tooltipXPct}%`, transform: alignRight ? 'translateX(-100%)' : 'translateX(-50%)', minWidth: 160 }}>
            <p className="font-semibold">{monthLabel}</p>
            {segments.map((seg) => (
              <div key={seg.name} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <span className="h-1.5 w-1.5 rounded-sm inline-block shrink-0" style={{ background: colorMap.get(seg.name) }} />
                  {seg.name}
                </div>
                <span className="font-mono tabular-nums">{seg.hours.toFixed(1)}h</span>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

// ─── Lightweight data hooks ───────────────────────────────────────────────────
function useProjects() {
  return useQuery({
    queryKey: ['projects-list'],
    queryFn: () =>
      customInstance<{ data: { items: Array<{ id: string; incomeYear: string; name: string; startDate: string; status: string; firstEntryAt: string | null }> } }>({
        url: '/api/projects',
        method: 'GET',
      }).then((r: { data: { items: Array<{ id: string; incomeYear: string; name: string; startDate: string; status: string; firstEntryAt: string | null }> } }) => r.data.items),
    staleTime: 30_000,
  });
}

function useArtefactCount() {
  return useQuery({
    queryKey: ['artefacts-count'],
    queryFn: () =>
      customInstance<{ data: { items: unknown[]; pagination?: { total?: number } } }>({
        url: '/api/artefacts?limit=1',
        method: 'GET',
      }).then(
        (r: { data: { items: unknown[]; pagination?: { total?: number } } }) =>
          r.data.pagination?.total ?? r.data.items.length,
      ),
    staleTime: 30_000,
  });
}

// ─── Payroll cost (Xero wages + super/on-costs + est. accrued leave) ──────────
// Loaded employment cost — the SAME basis as the claim reconciliation widget, so the
// dashboard overlay and the portal never show different payroll numbers.
function usePayrollCost(incomeYear: string) {
  return useQuery({
    queryKey: ['payroll-cost', incomeYear],
    queryFn: () =>
      customInstance<{ data: {
        payrollTotal: number; grossWagesTotal: number; superTotal: number; workersCompTotal: number;
        onCostsTotal: number; leaveAccrualTotal: number; leaveBasis?: 'actual_bs' | 'estimated_4_52';
        byMonth: Array<{ period: string; loadedCost: number }>;
      } }>({
        url: `/api/dashboard/payroll-cost?incomeYear=${incomeYear}`,
        method: 'GET',
      }).then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    enabled: !!incomeYear,
  });
}

// ─── Cloud cost summary ───────────────────────────────────────────────────────
function useCloudCosts(incomeYear: string) {
  return useQuery({
    queryKey: ['cloud-costs', incomeYear],
    queryFn: () =>
      customInstance<{
        data: {
          totalAud: number;
          byProvider: Record<string, number>;
          pendingCount: number;
          lastSyncedAt: string | null;
          configuredProviders: string[];
        };
      }>({ url: `/api/dashboard/cloud-costs?incomeYear=${incomeYear}`, method: 'GET' }),
    staleTime: 5 * 60 * 1000,
  });
}

const PROVIDER_META: Array<{ key: string; label: string; color: string }> = [
  { key: 'api_aws', label: 'AWS', color: '#f59e0b' },
  { key: 'api_gcp', label: 'GCP', color: '#3b82f6' },
  { key: 'api_openai', label: 'OpenAI', color: '#10b981' },
  { key: 'api_anthropic', label: 'Anthropic', color: '#8b5cf6' },
];


function CloudCostsSummary({ incomeYear }: { incomeYear: string }) {
  const { data, isLoading } = useCloudCosts(incomeYear);
  const [, navigate] = useLocation();

  if (isLoading) {
    return <div className="h-20 rounded-lg skeleton" />;
  }

  const summary = data?.data;
  const total = summary?.totalAud ?? 0;
  const pending = summary?.pendingCount ?? 0;
  const byProvider = summary?.byProvider ?? {};
  const configuredProviders = summary?.configuredProviders ?? [];

  if (configuredProviders.length === 0 && total === 0) return null;

  const providersWithData = PROVIDER_META.filter(({ key }) => (byProvider[key] ?? 0) > 0);

  if (total === 0 && pending === 0) {
    return (
      <div className="rounded-lg border bg-card px-4 py-3 flex items-center gap-3">
        <DollarSign className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{configuredProviders.join(', ')}</span> configured — costs sync nightly.{' '}
          <button onClick={() => navigate('/integrations')} className="underline hover:no-underline">
            Manage integrations
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2">
      <div className="flex items-center gap-2">
        <DollarSign className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Cloud costs {formatFy(incomeYear)}</span>
      </div>
      <span className="text-sm font-semibold font-mono tabular-nums">
        {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(total)}
      </span>
      {providersWithData.map(({ key, label, color }) => (
        <div key={key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} aria-hidden="true" />
          {label}:{' '}
          <span className="font-mono font-medium text-foreground">
            {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(byProvider[key] ?? 0)}
          </span>
        </div>
      ))}
      {pending > 0 && (
        <button onClick={() => navigate('/claims')} className="text-xs text-amber-600 font-medium hover:underline ml-auto">
          {pending} unattributed →
        </button>
      )}
      {summary?.lastSyncedAt && (
        <span className="text-xs text-muted-foreground ml-auto">
          synced {new Date(summary.lastSyncedAt).toLocaleDateString('en-AU')}
        </span>
      )}
    </div>
  );
}

// ─── Labour report ────────────────────────────────────────────────────────────
interface LabourProject {
  projectId: string;
  projectName: string;
  rawHours: number;
  cappedWeekdayHours: number;
  cappedWeekdayLabour: number;
  rdHours: number;
  rdLabour: number;
  rdPct: number;
}
interface LabourTotals {
  rawHours: number;
  cappedWeekdayHours: number;
  cappedWeekdayLabour: number;
  rdHours: number;
  rdLabour: number;
  rdPct: number;
  totalNonLabour: number;
  unratedRdHours: number;
  incomeYear: string;
  mode?: string;
}
interface LabourMonthItem {
  month: string;
  cappedWeekdayLabour: number;
  rdLabour: number;
  nonLabourExpenditure: number;
  nonLabourBySource: Record<string, number>;
  projectHours: Array<{ name: string; hours: number }>;
  personHours: Array<{ name: string; hours: number }>;
}

function useLabourReport(incomeYear: string) {
  return useQuery({
    queryKey: ['labour-report', incomeYear],
    queryFn: () =>
      customInstance<{ data: { projects: LabourProject[]; totals: LabourTotals; byMonth: LabourMonthItem[] } }>({
        url: `/api/dashboard/labour-report?incomeYear=${incomeYear}`,
        method: 'GET',
      }),
    staleTime: 5 * 60 * 1000,
    enabled: !!incomeYear,
  });
}

// ─── Insight section components (ported from insights page) ──────────────────

function fmtHours(h: number): string {
  return `${new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 }).format(h)} h`;
}

function momBadge(current: number, prev: number) {
  if (prev < 5) return null;
  const pct = Math.round(((current - prev) / prev) * 100);
  if (pct > 2)
    return {
      label: pct > 200 ? `↑ ${Math.round(current / prev)}x` : `↑ ${pct}%`,
      cls: 'text-green-600 bg-green-500/10',
    };
  if (pct < -2)
    return { label: `↓ ${Math.abs(pct)}%`, cls: 'text-red-600 bg-red-500/10' };
  return { label: '→ flat', cls: 'text-muted-foreground bg-muted/30' };
}

const PEAK_DAY_NAMES: Record<number, string> = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 0: 'Sun' };

function fmtPeakHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

interface OverviewTopProject { name: string; rdHours: number; prevMonthHours: number }
interface OverviewActivity { name: string; rdHours: number }
interface OverviewData {
  topProjects: { items: OverviewTopProject[]; totalRdHours: number; rdIntensity: number };
  peakWindows: { cells: Array<{ day: number; hour: number; count: number }> };
  velocity: { months: Array<{ label: string; rdHours: number }>; momChange: number | null };
  classification: { rd: number; mixed: number; nonRd: number; avgConfidence: number | null; pendingCount: number };
  activities: { items: OverviewActivity[]; untaggedHours: number };
}

function TopProjectsSection({ items }: { items: OverviewTopProject[] }) {
  if (items.length === 0) {
    return (
      <Card variant="static" className="space-y-2">
        <p className="text-sm font-medium">Top R&amp;D Projects</p>
        <p className="text-xs text-muted-foreground">Approved hours by project, this financial year</p>
        <div className="flex items-center justify-center rounded-lg border border-dashed h-24">
          <p className="text-sm text-muted-foreground">No approved R&D time entries yet.</p>
        </div>
      </Card>
    );
  }
  const maxHours = Math.max(...items.map((i) => i.rdHours));
  return (
    <Card variant="static" className="space-y-4">
      <div>
        <p className="text-sm font-medium">Top R&amp;D Projects</p>
        <p className="text-xs text-muted-foreground mt-0.5">Approved hours by project, this financial year</p>
      </div>
      <div className="space-y-2.5">
        {items.map((item) => {
          const badge = momBadge(item.rdHours, item.prevMonthHours);
          return (
            <div key={item.name} className="flex items-center gap-2">
              <span className="text-xs w-36 truncate shrink-0 text-muted-foreground" title={item.name}>
                {item.name}
              </span>
              <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-primary h-1.5 rounded-full transition-all"
                  style={{ width: `${maxHours > 0 ? (item.rdHours / maxHours) * 100 : 0}%` }}
                />
              </div>
              <span className="text-xs font-mono tabular-nums text-foreground w-14 text-right shrink-0">
                {fmtHours(item.rdHours)}
              </span>
              {badge && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full shrink-0 ${badge.cls}`}>
                  {badge.label}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function PeakWindowsSection({ cells }: { cells: Array<{ day: number; hour: number; count: number }> }) {
  const topCells = [...cells].sort((a, b) => b.count - a.count).slice(0, 3);
  return (
    <Card variant="static" className="space-y-4">
      <div>
        <p className="text-sm font-medium">Peak Working Hours</p>
        <p className="text-xs text-muted-foreground mt-0.5">Busiest R&D time blocks across the team</p>
      </div>
      {cells.length === 0 ? (
        <div className="flex items-center justify-center rounded-lg border border-dashed h-32">
          <p className="text-sm text-muted-foreground">No approved time entries yet.</p>
        </div>
      ) : (
        <>
          <WeekHeatmapChart cells={cells} />
          <div className="flex items-center gap-2 justify-end">
            <span className="text-xs text-muted-foreground">Low</span>
            <div className="flex gap-0.5">
              {[0.15, 0.35, 0.55, 0.75, 1].map((opacity) => (
                <span key={opacity} className="w-3.5 h-3.5 rounded-sm bg-primary" style={{ opacity }} aria-hidden="true" />
              ))}
            </div>
            <span className="text-xs text-muted-foreground">High</span>
          </div>
          {topCells.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Peak slots:{' '}
              {topCells.map((c) => `${PEAK_DAY_NAMES[c.day] ?? ''} ${fmtPeakHour(c.hour)} (${c.count})`).join(', ')}
            </p>
          )}
        </>
      )}
    </Card>
  );
}

function ClassificationSection({ rd, mixed, nonRd, avgConfidence, pendingCount }: {
  rd: number; mixed: number; nonRd: number; avgConfidence: number | null; pendingCount: number;
}) {
  const hasData = rd > 0 || mixed > 0 || nonRd > 0;
  return (
    <Card variant="static" className="space-y-4">
      <div>
        <p className="text-sm font-medium">Classification Breakdown</p>
        <p className="text-xs text-muted-foreground mt-0.5">Approved time entries by classification label</p>
      </div>
      {!hasData ? (
        <div className="flex items-center justify-center rounded-lg border border-dashed h-24">
          <p className="text-sm text-muted-foreground">No classification results yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex h-4 rounded-full overflow-hidden gap-px">
            {rd > 0 && <div className="bg-green-500" style={{ flex: rd }} title={`R&D — ${rd}%`} aria-label={`R&D ${rd}%`} />}
            {mixed > 0 && <div className="bg-amber-400" style={{ flex: mixed }} title={`Mixed — ${mixed}%`} aria-label={`Mixed ${mixed}%`} />}
            {nonRd > 0 && <div className="bg-red-400" style={{ flex: nonRd }} title={`Non-R&D — ${nonRd}%`} aria-label={`Non-R&D ${nonRd}%`} />}
            {pendingCount > 0 && <div className="bg-muted-foreground/20" style={{ flex: pendingCount }} title={`Pending — ${pendingCount}`} aria-label={`Pending ${pendingCount}`} />}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {rd > 0 && <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-green-500 shrink-0" aria-hidden="true" />R&amp;D {rd}%</span>}
            {mixed > 0 && <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400 shrink-0" aria-hidden="true" />Mixed {mixed}%</span>}
            {nonRd > 0 && <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-400 shrink-0" aria-hidden="true" />Non-R&amp;D {nonRd}%</span>}
            {pendingCount > 0 && <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-muted-foreground/20 shrink-0" aria-hidden="true" />Pending {pendingCount}</span>}
          </div>
          {avgConfidence !== null && (
            <div className="pt-1 border-t flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Avg confidence</span>
              <span className="text-xs font-semibold font-mono tabular-nums">{avgConfidence.toFixed(1)}%</span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function ActivityBreakdownSection({ items, untaggedHours }: { items: OverviewActivity[]; untaggedHours: number }) {
  const allItems = [...items, ...(untaggedHours > 0 ? [{ name: 'Untagged', rdHours: untaggedHours }] : [])];
  const maxHours = Math.max(...allItems.map((i) => i.rdHours), 1);
  return (
    <Card variant="static" className="space-y-4">
      <div>
        <p className="text-sm font-medium">R&amp;D by Activity</p>
        <p className="text-xs text-muted-foreground mt-0.5">Where R&D hours are being spent this financial year</p>
      </div>
      {allItems.length === 0 ? (
        <div className="flex items-center justify-center rounded-lg border border-dashed h-24">
          <p className="text-sm text-muted-foreground">No activity-tagged time entries yet.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {allItems.map((item) => (
            <div key={item.name} className="flex items-center gap-2">
              <span
                className={`text-xs w-36 truncate shrink-0 ${item.name === 'Untagged' ? 'text-muted-foreground/60 italic' : 'text-muted-foreground'}`}
                title={item.name}
              >
                {item.name}
              </span>
              <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-1.5 rounded-full transition-all ${item.name === 'Untagged' ? 'bg-muted-foreground/30' : 'bg-primary/60'}`}
                  style={{ width: `${(item.rdHours / maxHours) * 100}%` }}
                />
              </div>
              <span className="text-xs font-mono tabular-nums text-foreground w-14 text-right shrink-0">
                {fmtHours(item.rdHours)}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── Defensibility matrix ─────────────────────────────────────────────────────
// Types + score labels come from the shared @/lib/defensibility module. The matrix keeps its
// own compact column labels + order and colour-pill styling (a dashboard-specific presentation
// distinct from the widget/evidence-log's icon+badge rendering).
interface DefensibilityCheck {
  criterion: DefensibilityCriterion;
  score: DefensibilityScore;
}

interface ProjectDefensibility {
  projectId: string;
  projectName: string;
  checks: DefensibilityCheck[];
}

const CRITERIA: { key: DefensibilityCriterion; label: string }[] = [
  { key: 'new_knowledge', label: 'New knowledge' },
  { key: 'technical_uncertainty', label: 'Tech uncertainty' },
  { key: 'systematic_progression', label: 'Systematic' },
  { key: 'contemporaneous_documentation', label: 'Documentation' },
];

// Colour pills are dashboard-specific; the label text is sourced from the shared SCORE_META.
const SCORE_STYLES: Record<DefensibilityScore, { bg: string; fg: string }> = {
  strong:   { bg: 'bg-[oklch(0.95_0.03_155)]', fg: 'text-[oklch(0.40_0.11_155)]' },
  moderate: { bg: 'bg-[oklch(0.96_0.05_80)]',  fg: 'text-[oklch(0.50_0.11_72)]' },
  weak:     { bg: 'bg-[oklch(0.95_0.06_50)]',  fg: 'text-[oklch(0.55_0.14_45)]' },
  gap:      { bg: 'bg-[oklch(0.95_0.03_25)]',  fg: 'text-[oklch(0.55_0.16_25)]' },
};

function ScorePill({ score }: { score: DefensibilityScore }) {
  const s = SCORE_STYLES[score];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${s.bg} ${s.fg}`}>
      {SCORE_META[score].label}
    </span>
  );
}

function useProjectDefensibilityMatrix(projectIds: string[]) {
  return useQuery({
    queryKey: ['defensibility-matrix', projectIds.join(',')],
    queryFn: async () => {
      const results = await Promise.all(
        projectIds.map(id =>
          customInstance<{ data: { checks: DefensibilityCheck[] } }>({
            url: `/api/projects/${id}/defensibility`,
            method: 'GET',
          }).then(r => ({ projectId: id, checks: r.data.checks }))
        )
      );
      return results;
    },
    enabled: projectIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

interface DefensibilityProject { id: string; name: string }

function DefensibilityMatrixSection({ projects }: { projects: DefensibilityProject[] }) {
  const projectIds = projects.map(p => p.id);
  const { data: matrixData, isLoading } = useProjectDefensibilityMatrix(projectIds);

  if (projects.length === 0) return null;

  const rows: ProjectDefensibility[] = (matrixData ?? []).map((d, i) => ({
    projectId: d.projectId,
    projectName: projects[i]?.name ?? d.projectId,
    checks: d.checks,
  }));

  return (
    <section className="space-y-3">
      <h2 className="text-[13px] font-semibold text-foreground uppercase tracking-[0.04em] text-muted-foreground">
        Claim Defensibility
      </h2>
      {isLoading ? (
        <SkeletonCard className="h-32" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No defensibility data yet"
          body="Defensibility scores appear after the first eligibility check runs for a project."
        />
      ) : (
        <div className="rounded-[14px] border border-border bg-card overflow-hidden" style={{ boxShadow: '0 12px 40px oklch(0.2 0.03 260 / 0.06)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground w-48">Project</th>
                  {CRITERIA.map(c => (
                    <th key={c.key} className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map(row => (
                  <tr key={row.projectId} className="hover:bg-[oklch(0.98_0.004_250)] transition-colors">
                    <td className="px-4 py-3">
                      <Link href={`/projects/${row.projectId}`}>
                        <span className="text-sm font-medium text-foreground hover:text-primary transition-colors cursor-pointer truncate block max-w-[180px]">
                          {row.projectName}
                        </span>
                      </Link>
                    </td>
                    {CRITERIA.map(c => {
                      const check = row.checks.find(ch => ch.criterion === c.key);
                      return (
                        <td key={c.key} className="px-4 py-3 text-center">
                          {check ? <ScorePill score={check.score} /> : <span className="text-xs text-muted-foreground">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

// ─── Main dashboard ───────────────────────────────────────────────────────────
export function DashboardPage() {
  const { user } = useAuthStore();
  const { user: clerkUser } = useUser();
  const [, navigate] = useLocation();

  const { data: projectsData, isLoading: loadingProjects } = useProjects();
  const { data: artefactCountData } = useArtefactCount();

  const projectCount = projectsData?.length ?? 0;

  const [selectedFy, setSelectedFy] = useState<string | null>(null);

  // Build sorted list of FYs that have projects, always including the current FY.
  const projectYears = [...new Set(
    (projectsData ?? [])
      .map((p) => p.incomeYear)
      .filter((y): y is string => typeof y === 'string' && /^FY\d{4}$/.test(y)),
  )].sort();
  const curFy = currentIncomeYear();
  const availableYears = projectYears.includes(curFy) ? projectYears : [...projectYears, curFy].sort();

  // During July-October, default to the prior FY — it just ended and is being filed.
  // The rest of the year, default to current FY.
  const defaultFy = (() => {
    const month = new Date().getMonth(); // 0-indexed; July=6
    const year = new Date().getFullYear();
    if (month >= 6 && month <= 9) return `FY${year}`; // prior FY
    return curFy;
  })();
  const incomeYear =
    selectedFy ??
    (availableYears.includes(defaultFy) ? defaultFy : availableYears.at(-1) ?? defaultFy);
  const fyIdx = availableYears.indexOf(incomeYear);
  const hasPrev = fyIdx > 0;
  const hasNext = fyIdx < availableYears.length - 1;

  const { data: labourReportData, isLoading: loadingLabourReport } = useLabourReport(incomeYear);
  const { data: payrollCost } = usePayrollCost(incomeYear);

  // Month → loaded employment cost (wages + super/on-costs + est. leave) for the payroll overlay
  const payrollByMonth: Record<string, number> = {};
  for (const item of payrollCost?.byMonth ?? []) {
    payrollByMonth[item.period] = item.loadedCost;
  }
  const totalPayroll = payrollCost?.payrollTotal ?? 0;

  // Summary (FY-filtered): approved count and pending count for the income year.
  const { data: summaryData, isLoading: loadingSummary } = useQuery({
    queryKey: ['time-entries-summary', incomeYear],
    queryFn: () =>
      customInstance<{
        data: {
          items: Array<{
            projectId: string; projectName: string;
            pendingCount: number; pendingHours: number;
            approvedCount: number; approvedHours: number;
            rdHours: number; classifyingCount: number;
          }>;
          totals: {
            pendingCount: number; pendingHours: number;
            approvedCount: number; approvedHours: number;
            rdHours: number; classifyingCount: number;
          };
        };
      }>({ url: `/api/time-entries/summary?incomeYear=${incomeYear}`, method: 'GET' }),
    staleTime: 60_000,
    enabled: !!incomeYear,
  });

  const { data: pendingDelibData } = useGetMyPendingDeliberations();
  const pendingDelibs = pendingDelibData?.data?.items ?? [];

  type ActionItem = { type: string; priority: number; title: string; sub: string; href: string; ctaLabel: string; severity: 'urgent' | 'warn' | 'info' };
  const { data: actionItemsData } = useQuery({
    queryKey: ['dashboard-action-items', incomeYear],
    queryFn: () =>
      customInstance<{ data: { items: ActionItem[]; pendingCount: number; openClaimCount: number } }>({
        url: `/api/dashboard/action-items?incomeYear=${incomeYear}`,
        method: 'GET',
      }),
    staleTime: 60_000,
    enabled: !!incomeYear,
  });
  const actionItems = actionItemsData?.data?.items ?? [];

  // Insights overview — full data for all 4 analytics panels
  const { data: overviewData } = useQuery({
    queryKey: ['insights-overview', incomeYear],
    queryFn: () =>
      customInstance<{ data: OverviewData }>({
        url: `/api/insights/overview?incomeYear=${incomeYear}`,
        method: 'GET',
      }),
    staleTime: 120_000,
    enabled: !!incomeYear,
  });
  const topProjects = overviewData?.data?.topProjects?.items ?? [];
  const peakCells = overviewData?.data?.peakWindows?.cells ?? [];
  const classification = overviewData?.data?.classification;
  const topActivities = overviewData?.data?.activities?.items ?? [];
  const untaggedHours = overviewData?.data?.activities?.untaggedHours ?? 0;
  const hasOverviewData = topProjects.length > 0 || peakCells.length > 0 || (classification && (classification.rd + classification.mixed + classification.nonRd + classification.pendingCount) > 0) || topActivities.length > 0;

  const artefactCount = artefactCountData ?? 0;

  // Derived from the FY-filtered summary (approved/pending counts)
  const summaryTotals = summaryData?.data?.totals;
  const pendingCount = summaryTotals?.pendingCount ?? 0;
  const approvedCount = summaryTotals?.approvedCount ?? 0;
  const loadingApproved = loadingSummary;


  // Expenditure from capped labour report (interval-union + 7.6h/day cap)
  const labourTotals = labourReportData?.data?.totals;
  const labourByMonth = labourReportData?.data?.byMonth ?? [];
  const totalLabour = labourTotals?.rdLabour ?? 0;
  const totalNonLabour = labourTotals?.totalNonLabour ?? 0;
  const totalGrossLabour = labourTotals?.cappedWeekdayLabour ?? 0;
  const totalExpenditure = totalLabour + totalNonLabour;
  const totalGrossExpenditure = totalGrossLabour + totalNonLabour;
  const showRange = totalGrossExpenditure - totalExpenditure > 1;
  const avgRdRate = labourTotals?.rdPct ?? 0;
  const totalEligibleHours = labourTotals?.cappedWeekdayHours ?? 0;
  // R&D-classified hours (capped + deduped, ATO basis) — what directly drives the dollar figure
  const totalRdHours = labourTotals?.rdHours ?? 0;

  // Build MonthItem array for chart from capped labour report byMonth data
  const timeline: MonthItem[] = labourByMonth.map((m) => ({
    month: m.month,
    labourExpenditure: m.rdLabour,
    grossLabourExpenditure: m.cappedWeekdayLabour,
    nonLabourExpenditure: m.nonLabourExpenditure,
    nonLabourBySource: m.nonLabourBySource,
    totalExpenditure: m.rdLabour + m.nonLabourExpenditure,
    payrollExpenditure: payrollByMonth[m.month] ?? 0,
    projectHours: m.projectHours ?? [],
    personHours: m.personHours ?? [],
  }));

  const hasAnyData = projectCount > 0 || artefactCount > 0;

  const hasActionItems = actionItems.length > 0 || pendingDelibs.length > 0;

  const firstName = user?.name?.split(' ')[0] || clerkUser?.firstName || '';

  return (
    <div className="space-y-6">
      {/* ── Greeting ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {getGreeting()}{firstName ? `, ${firstName}` : ''}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{formatTodayLong()}</p>
        </div>
        {availableYears.length > 1 && (
          <div className="flex items-center gap-0.5 rounded-lg border px-1 py-0.5 text-sm shrink-0">
            <button
              onClick={() => hasPrev && setSelectedFy(availableYears[fyIdx - 1]!)}
              disabled={!hasPrev}
              className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous financial year"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <span className="px-2 font-medium tabular-nums text-sm">{formatFy(incomeYear)}</span>
            <button
              onClick={() => hasNext && setSelectedFy(availableYears[fyIdx + 1]!)}
              disabled={!hasNext}
              className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Next financial year"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* ── Setup checklist ───────────────────────────────────────────────── */}
      <SetupChecklist />

      {/* ── What needs you ───────────────────────────────────────────────── */}
      {hasActionItems && (
        <div className="rounded-xl border bg-card divide-y divide-border">
          <div className="flex items-center gap-2 px-4 pt-3 pb-2">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              What needs you
            </p>
            <span className="text-[10.5px] font-semibold text-muted-foreground bg-muted rounded-full px-1.5 py-0.5 leading-none">
              {actionItems.length + pendingDelibs.length}
            </span>
          </div>
          {actionItems.map((item) => {
            const iconEl = item.type === 'time_review' ? (
              <Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            ) : item.type === 'tfa_panelist' ? (
              <Users className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            ) : item.type === 'assessor_pending' ? (
              <CheckCircle2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            ) : item.type === 'claim_blocker' ? (
              <ShieldAlert className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            ) : (
              <AlertCircle className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            );
            return (
              <div key={item.type + item.href} className="flex items-center gap-3 px-4 py-3">
                <div className="shrink-0 w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                  {iconEl}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-snug">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.sub}</p>
                </div>
                <button
                  onClick={() => navigate(item.href)}
                  className="shrink-0 text-xs font-semibold text-primary hover:underline"
                >
                  {item.ctaLabel} →
                </button>
              </div>
            );
          })}
          {pendingDelibs.map((d) => (
            <div key={d.projectId} className="flex items-center gap-3 px-4 py-3">
              <div className="shrink-0 w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                <Scale className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-snug">TFA deliberation pending</p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{d.projectName}</p>
              </div>
              <button
                onClick={() => navigate(`/projects/${d.projectId}`)}
                className="shrink-0 text-xs font-semibold text-primary hover:underline"
              >
                Go to TFA →
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Metric cards ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-tour="dashboard-stats">
        <div className="animate-card-enter stagger-1">
          <StatCard
            label="Total R&D Hours"
            value={totalRdHours > 0 ? new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 }).format(totalRdHours) : '—'}
            secondary={totalRdHours > 0 ? `${formatFy(incomeYear)} · capped & classified` : undefined}
            icon={TrendingUp}
            href="/timewriting"
            isLoading={loadingLabourReport}
          />
        </div>
        <div className="animate-card-enter stagger-2">
          <StatCard
            label="R&D Intensity"
            value={avgRdRate > 0 ? `${avgRdRate.toFixed(0)}` : '—'}
            unit={avgRdRate > 0 ? '%' : undefined}
            secondary={avgRdRate > 0 ? `of capped hours are R&D` : undefined}
            icon={TrendingUp}
            href="/timewriting"
            isLoading={loadingLabourReport}
          />
        </div>
        <div className="animate-card-enter stagger-3">
          <StatCard
            label="Pending Review"
            value={pendingCount}
            secondary={pendingCount > 0 ? 'time entries to action' : undefined}
            icon={Clock}
            href="/timewriting"
            isLoading={loadingApproved}
            alert={pendingCount > 50}
          />
        </div>
        <div className="animate-card-enter stagger-4">
          <StatCard
            label="Approved Entries"
            value={approvedCount}
            secondary={approvedCount > 0 ? `${projectCount} active project${projectCount !== 1 ? 's' : ''}` : undefined}
            icon={CheckCircle2}
            href="/timewriting"
            isLoading={loadingApproved}
          />
        </div>
      </div>

      {/* ── FY claim status ───────────────────────────────────────────────── */}
      <ClaimHealthBanner
        incomeYear={incomeYear}
        totalLabour={totalLabour}
        totalGrossLabour={totalGrossLabour}
        totalNonLabour={totalNonLabour}
        avgRdRate={avgRdRate}
        approvedCount={approvedCount}
        totalEligibleHours={totalEligibleHours}
        isLoading={loadingLabourReport || loadingApproved}
      />

      {/* ── Defensibility matrix ─────────────────────────────────────────── */}
      {projectCount > 0 && (
        <DefensibilityMatrixSection
          projects={(projectsData ?? [])
            .filter(p => p.status === 'active' && p.incomeYear === incomeYear)
            .map(p => ({ id: p.id, name: p.name }))}
        />
      )}

      {/* ── Insights & trends divider ────────────────────────────────────── */}
      <div className="flex items-center gap-4 pt-2">
        <div className="flex-1 border-t border-border" />
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          Insights &amp; trends
        </span>
        <div className="flex-1 border-t border-border" />
      </div>

      {/* Expenditure chart — full width */}
      <Card variant="static" className="p-5 space-y-4">
        {!loadingLabourReport && totalExpenditure > 0 ? (
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                Monthly R&D expenditure — {formatFy(incomeYear)}
              </p>
              {showRange ? (
                <div className="flex items-baseline gap-2 flex-wrap">
                  <p className="text-2xl font-semibold font-mono tabular-nums text-primary">
                    {formatAUD(totalExpenditure)}
                  </p>
                  <span className="text-sm text-muted-foreground">classified</span>
                  <span className="text-muted-foreground/40 text-sm">·</span>
                  <p className="text-xl font-semibold font-mono tabular-nums text-muted-foreground">
                    {formatAUD(totalGrossExpenditure)}
                  </p>
                  <span className="text-sm text-muted-foreground">all R&D project labour</span>
                </div>
              ) : (
                <p className="text-2xl font-semibold font-mono tabular-nums text-primary">
                  {formatAUD(totalExpenditure)}
                </p>
              )}
            </div>
            <div className="flex gap-5 pb-1">
              <div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-0.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-primary/85 inline-block" aria-hidden="true" />
                  Labour
                </div>
                <p className="font-mono font-semibold text-sm tabular-nums">{formatAUD(totalLabour)}</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-0.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-blue-400/50 inline-block" aria-hidden="true" />
                  Non-labour
                </div>
                <p className="font-mono font-semibold text-sm tabular-nums">{formatAUD(totalNonLabour)}</p>
              </div>
              {totalPayroll > 0 && (
                <div className="relative group cursor-help" tabIndex={0} aria-describedby="payroll-cost-breakdown">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-0.5">
                    <svg width="18" height="10" viewBox="0 0 18 10" aria-hidden="true" className="inline-block shrink-0">
                      <polyline points="0,5 9,5 18,5" fill="none" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.75" />
                      <circle cx="9" cy="5" r="3" fill="#f59e0b" fillOpacity="0.9" stroke="white" strokeWidth="1" />
                    </svg>
                    Payroll cost (incl. super + leave)
                  </div>
                  <p className="font-mono font-semibold text-sm tabular-nums border-b border-dotted border-muted-foreground/50 inline-block">{formatAUD(totalPayroll)}</p>
                  {/* Hover/focus breakdown: exactly what the total is made of, on the same basis
                      as the claim reconciliation. Wages are GROSS — PAYG withheld is inside them
                      (remitted to the ATO via BAS), never an additive component. */}
                  <div
                    id="payroll-cost-breakdown"
                    role="tooltip"
                    className="pointer-events-none absolute right-0 top-full z-20 mt-1.5 hidden w-80 rounded-md border bg-popover p-3 text-xs shadow-md group-hover:block group-focus-within:block"
                  >
                    <p className="font-medium mb-2">Total payroll cost — {formatAUD(totalPayroll)}</p>
                    <div className="space-y-1 tabular-nums">
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Gross wages (incl. PAYG withheld)</span>
                        <span className="font-mono">{formatAUD(payrollCost?.grossWagesTotal ?? 0)}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Superannuation</span>
                        <span className="font-mono">{formatAUD(payrollCost?.superTotal ?? 0)}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Workers comp</span>
                        <span className="font-mono">{(payrollCost?.workersCompTotal ?? 0) > 0 ? formatAUD(payrollCost!.workersCompTotal) : '— (in Insurance, not separable)'}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">
                          {payrollCost?.leaveBasis === 'actual_bs' ? 'Leave liability movement (balance sheet)' : 'Accrued leave (est. 4 wks/yr)'}
                        </span>
                        <span className="font-mono">{formatAUD(payrollCost?.leaveAccrualTotal ?? 0)}</span>
                      </div>
                    </div>
                    <p className="mt-2 text-muted-foreground">
                      PAYG withholding is part of gross wages — the quarterly BAS payments remit it to the ATO; adding them separately would double-count. Payroll tax: nil (WA, under threshold).
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <h2 className="text-sm font-medium">Monthly R&D expenditure — {formatFy(incomeYear)}</h2>
          </div>
        )}

        {loadingLabourReport ? (
          <div className="rounded skeleton" style={{ height: 240 }} />
        ) : timeline.length > 0 && totalExpenditure > 0 ? (
          <SimpleRdChart items={timeline} />
        ) : (
          <div
            className="flex items-center justify-center rounded-lg border border-dashed"
            style={{ height: 160 }}
          >
            <div className="text-center">
              <p className="text-sm text-muted-foreground">No expenditure data for {formatFy(incomeYear)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Approve timewriting and add cost allocations to see the chart.
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* Hours by project / person */}
      {!loadingLabourReport && timeline.length > 0 && (
        <Card variant="static" className="p-5">
          <ProjectPersonChart items={timeline} />
        </Card>
      )}

      {/* Cloud cost summary */}
      <CloudCostsSummary incomeYear={incomeYear} />

      {/* SDLC stage breakdown (claim-wide, indicative recorded work time) */}
      {incomeYear && <SdlcBreakdown incomeYear={incomeYear} />}

      {/* 4-panel insight grid */}
      {hasOverviewData && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="animate-card-enter stagger-1">
              <TopProjectsSection items={topProjects} />
            </div>
            <div className="animate-card-enter stagger-2">
              <PeakWindowsSection cells={peakCells} />
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="animate-card-enter stagger-3">
              <ClassificationSection
                rd={classification?.rd ?? 0}
                mixed={classification?.mixed ?? 0}
                nonRd={classification?.nonRd ?? 0}
                avgConfidence={classification?.avgConfidence ?? null}
                pendingCount={classification?.pendingCount ?? 0}
              />
            </div>
            <div className="animate-card-enter stagger-4">
              <ActivityBreakdownSection items={topActivities} untaggedHours={untaggedHours} />
            </div>
          </div>
        </>
      )}

      {/* Getting started — shown only when no data yet */}
      {!loadingProjects && !hasAnyData && (
        <Card variant="static" className="border-dashed p-8">
          <h2 className="font-medium mb-4">Get started</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">1</div>
                <p className="font-medium text-sm">Create a project</p>
              </div>
              <p className="text-xs text-muted-foreground pl-9">
                Set up an R&D project and assign it to an income year (e.g. FY2025).
              </p>
              <button
                onClick={() => navigate('/projects/new')}
                className="ml-9 inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <Plus className="h-3 w-3" aria-hidden="true" /> New project
              </button>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">2</div>
                <p className="font-medium text-sm">Connect a signal source</p>
              </div>
              <p className="text-xs text-muted-foreground pl-9">
                Link a GitHub repository to automatically capture commits as evidence.
              </p>
              <button
                onClick={() => navigate('/signals')}
                className="ml-9 inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <Plus className="h-3 w-3" aria-hidden="true" /> Add signal source
              </button>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">3</div>
                <p className="font-medium text-sm">Complete a TFA</p>
              </div>
              <p className="text-xs text-muted-foreground pl-9">
                Lock a Technical Feasibility Assessment before classifying evidence as R&D.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
